
import { computed, defineComponent, PropType, onMounted } from "vue";
import {
  getCountryCodeForRegionCode,
  parsePhoneNumber
} from "awesome-phonenumber";
import AdmTextField from "@afe/admirals-dev/src/components/AdmTextField";
import countries from "./countries";
import "./phone.scss";

const parsePhoneNumberSafe = (number: string) => {
  const pn = parsePhoneNumber(number);

  return pn.valid
    ? pn
    : { number: { input: number, international: "", e164: "" } };
};

export default defineComponent({
  name: "adm-phone",
  components: {
    AdmTextField
  },
  emits: ["update:modelValue"],
  model: {
    prop: "modelValue",
    event: "update:modelValue"
  },
  props: {
    id: {
      type: String,
      required: true
    },
    defaultCountry: {
      type: String,
      default: ""
    },
    modelValue: {
      type: String,
      default: ""
    },
    size: {
      type: String as PropType<"small" | "normal">,
      default: "normal"
    },
    dense: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    helperText: {
      type: String,
      default: ""
    },
    error: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const value = computed({
      get() {
        const { number } = parsePhoneNumberSafe(props.modelValue);

        if (!number) {
          return "";
        }

        return number.international || number.input;
      },
      set(nextValue: string) {
        const phoneValue =
          nextValue.indexOf("+") !== -1 ? nextValue : `+${nextValue}`;
        const { number } = parsePhoneNumberSafe(phoneValue);
        const parserNumber = number ? number.e164 || number.input : "";

        emit("update:modelValue", nextValue ? parserNumber : "");
      }
    });

    const countryValue = computed({
      get() {
        const { regionCode } = parsePhoneNumber(`${value.value} `);

        return regionCode;
      },
      set(nextValue?: string) {
        const dialCode = getCountryCodeForRegionCode(nextValue || "");

        emit("update:modelValue", `+${dialCode} `);
      }
    });

    const countryFlag = computed(() => {
      return countryValue.value
        ? `https://static-images.admiralmarkets.com/data/flags/svg/1x1/${countryValue.value}.svg`.toLowerCase()
        : `https://static-images.admiralmarkets.com/data/flags/svg/1x1/international.svg`;
    });

    onMounted(() => {
      if (props.defaultCountry && !props.modelValue) {
        const dialCode = getCountryCodeForRegionCode(props.defaultCountry);

        emit("update:modelValue", `+${dialCode} `);
      }
    });

    const isNumber = (event: KeyboardEvent): void => {
      const keysAllowed: string[] = [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9"
      ];
      const keyPressed: string = event.key;

      if (keyPressed === "+" && value.value === "") {
        return;
      }

      if (!keysAllowed.includes(keyPressed)) {
        event.preventDefault();
      }
    };

    return {
      value,
      countryFlag,
      countryValue,
      countries,
      isNumber
    };
  }
});
